import React, { useEffect, useRef } from 'react';
import fetchWithToken from '../../services';

const EmailChangeModal = ({ isOpen, currentEmail, step, verificationCode, setVerificationCode, newEmail, setNewEmail, setOriginalEmail, setIsEmailModalOpen, sendVerificationCode, email, setEmail, emailStep, setEmailStep, isLoading, setIsLoading, error, setEmailError }) => {
  const modalRef = useRef(null);
  const triggerRef = useRef(null);
  const currentEmailCodeRef = useRef('');

  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement;
      setTimeout(() => {
        modalRef.current?.querySelector('h3')?.focus();
      }, 100);
    } else {
      triggerRef.current?.focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleModalClose();
      }

      if (event.key === 'Tab') {
        const focusableElements = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            event.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            event.preventDefault();
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const verifyCurrentEmail = () => {
    if (!verificationCode) {
      setEmailError('Please enter the verification code');
      return;
    }
    // Hold the code locally — it will be validated server-side when the new email is submitted
    currentEmailCodeRef.current = verificationCode;
    setEmailStep('enter-new-email');
    setVerificationCode('');
  };

  const handleNewEmailSubmit = async () => {
    if (!newEmail) {
      setEmailError('Please enter a new email address');
      return;
    }

    // Simple email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setEmailError('');

    try {
      // Check if email is already in use
      const response = await fetchWithToken('/check-email-availability', {
        method: 'POST',
        body: JSON.stringify({ email: newEmail }),
      });

      const data = await response.json();

      if (!data.available) {
        setEmailError('This email address is already in use');
        return;
      }

      // If email is available, send verification to the new email.
      // currentEmailCode is validated server-side to enforce the full flow.
      const sendRes = await fetchWithToken('/send-email-change-verification-code', {
        method: 'POST',
        body: JSON.stringify({ email: newEmail, currentEmailCode: currentEmailCodeRef.current }),
      });

      if (!sendRes.ok) {
        const sendData = await sendRes.json().catch(() => ({}));
        setEmailError(sendData.error || 'Failed to send verification code');
        return;
      }

      setEmailStep('verify-new-email');
      setVerificationCode('');
    } catch (error) {
      console.error('Error updating email:', error);
      setEmailError('An error occurred while updating your email');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyNewEmail = async () => {
    if (!verificationCode) {
      setEmailError('Please enter the verification code');
      return;
    }

    setIsLoading(true);
    setEmailError('');

    try {
      const response = await fetchWithToken('/verify-email-change', {
        method: 'POST',
        body: JSON.stringify({
          code: verificationCode
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Update the email in the parent component
        setEmail(newEmail);
        setOriginalEmail(newEmail);
        setEmailStep('success');
      } else {
        setEmailError(data.error || 'Failed to update email');
      }
    } catch (error) {
      console.error('Error updating email:', error);
      setEmailError('An error occurred while updating your email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsEmailModalOpen(false);
    setEmailStep('verify-current');
    setVerificationCode('');
    setNewEmail('');
    setEmailError('');
    currentEmailCodeRef.current = '';
    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  };

  const handleModalVerify = () => {
    switch (emailStep) {
      case 'verify-current':
        verifyCurrentEmail();
        break;
      case 'enter-new-email':
        handleNewEmailSubmit();
        break;
      case 'verify-new-email':
        verifyNewEmail();
        break;
      case 'success':
        handleModalClose();
        break;
      default:
        break;
    }
  };

  const handleModalCancel = () => {
    if (emailStep === 'success') {
      handleModalClose();
    } else if (emailStep === 'enter-new-email' || emailStep === 'verify-new-email') {
      currentEmailCodeRef.current = '';
      setEmailStep('verify-current');
      setVerificationCode('');
      setNewEmail('');
      setEmailError('');
      sendVerificationCode(email);
    } else {
      handleModalClose();
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 'verify-current':
        return (
          <>
            <h3 id="modal-title-verify-current" tabIndex="-1" className="text-lg font-medium mb-2">Verify Your Current Email</h3>
            <p className="text-gray-600 mb-4">We've sent a verification code to {currentEmail}. Please enter it below to continue.</p>
            <div className="mb-4">
              <label htmlFor="current-email-verification" className="block text-sm font-medium text-gray-700 mb-1">Verification Code</label>
              <input
                id="current-email-verification"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder="Enter 6-digit code"
                maxLength="6"
              />
            </div>
            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={handleModalCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleModalVerify}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                disabled={isLoading}
              >
                {isLoading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </>
        );
      case 'enter-new-email':
        return (
          <>
            <h3 id="modal-title-enter-new-email" tabIndex="-1" className="text-lg font-medium mb-2">Enter New Email Address</h3>
            <div className="mb-4">
              <label htmlFor="new-email-input" className="block text-sm font-medium text-gray-700 mb-1">New Email</label>
              <input
                id="new-email-input"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder="new@example.com"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={handleModalCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleModalVerify}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                disabled={isLoading || !newEmail}
              >
                {isLoading ? 'Sending...' : 'Send Verification'}
              </button>
            </div>
          </>
        );
      case 'verify-new-email':
        return (
          <>
            <h3 id="modal-title-verify-new-email" tabIndex="-1" className="text-lg font-medium mb-2">Verify Your New Email</h3>
            <p className="text-gray-600 mb-4">We've sent a verification code to {newEmail}. Please enter it below to complete the email change.</p>
            <div className="mb-4">
              <label htmlFor="new-email-verification" className="block text-sm font-medium text-gray-700 mb-1">Verification Code</label>
              <input
                id="new-email-verification"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder="Enter 6-digit code"
                maxLength="6"
              />
            </div>
            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={handleModalCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleModalVerify}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                disabled={isLoading}
              >
                {isLoading ? 'Updating...' : 'Update Email'}
              </button>
            </div>
          </>
        );
      case 'success':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 id="modal-title-success" tabIndex="-1" className="text-lg font-medium text-gray-900 mt-4">Email Updated Successfully</h3>
            <p className="mt-2 text-sm text-gray-500">
              Your email address has been updated to {newEmail}.
            </p>
            <div className="mt-5">
              <button
                type="button"
                onClick={handleModalClose}
                className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
              >
                Done
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby={`modal-title-${emailStep.replace(/-/g, '')}`} role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={handleModalClose}></div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div ref={modalRef} className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};

export default EmailChangeModal;